import { Badge } from '@/components/ui/badge';
import { formatTimestamp } from '@/utils/dateUtils';

interface PickupExceptionPayloadProps {
  payload: Record<string, unknown>;
  userNameMap: Map<string, string>;
  i18n: { _: (d: { id: string; message: string }) => string };
}

/**
 * Local copy of WorkflowPayloadDetail's FieldRow. This is a detail panel, not a
 * data table, so BaseEditableTable does not apply and no raw <table> is used.
 */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Read a payload field as a non-blank string, or undefined. */
function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Renders a `pickup_exception` workflow payload (issue #993): a child was
 * released to someone NOT on the authorised pickup list. This is a notice, not
 * an approval gate — the handover already happened.
 */
export function PickupExceptionPayload({
  payload,
  userNameMap,
  i18n,
}: PickupExceptionPayloadProps) {
  const childName = readString(payload, 'child_name');
  const collectorName = readString(payload, 'collector_name');
  const explanation = readString(payload, 'explanation');
  const recordedBy = readString(payload, 'recorded_by');
  // The server resolves the recording employee's name into the payload at
  // creation time, so prefer it over the page's user map: the map only covers
  // users referenced by the current page and would otherwise fall through to an
  // opaque Clerk id on a safeguarding record.
  const recordedByName = readString(payload, 'recorded_by_name');
  const checkedOutAt = readString(payload, 'checked_out_at');

  // formatTimestamp (not formatDate) because this is a handover TIME, not a
  // date: "checked out at 16:45" is the safeguarding fact. Same locale-aware
  // parsing path — never new Date("YYYY-MM-DD").
  const checkedOutAtLabel = checkedOutAt ? formatTimestamp(checkedOutAt) : undefined;

  return (
    <div className="space-y-4">
      {childName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.childName', message: 'Child' })}>
          <p className="text-sm">{childName}</p>
        </FieldRow>
      )}

      {collectorName && (
        <FieldRow
          label={i18n._({ id: 'workflow.payload.collectorName', message: 'Collected by' })}
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm">{collectorName}</p>
            {/* Text-bearing, so the warning is never colour-only. */}
            <Badge variant="destructive">
              {i18n._({
                id: 'workflow.payload.notOnList',
                message: 'Not on the authorised list',
              })}
            </Badge>
          </div>
        </FieldRow>
      )}

      {explanation && (
        <FieldRow
          label={i18n._({ id: 'workflow.payload.explanation', message: 'Explanation' })}
        >
          {/* whitespace-pre-wrap so a multi-line explanation is not collapsed. */}
          <p className="text-sm whitespace-pre-wrap">{explanation}</p>
        </FieldRow>
      )}

      {(recordedByName || recordedBy) && (
        <FieldRow
          label={i18n._({ id: 'workflow.detail.recordedBy', message: 'Recorded By' })}
        >
          <p className="text-sm">
            {recordedByName ?? (recordedBy ? (userNameMap.get(recordedBy) ?? recordedBy) : '')}
          </p>
        </FieldRow>
      )}

      {checkedOutAtLabel && (
        <FieldRow
          label={i18n._({ id: 'workflow.payload.checkedOutAt', message: 'Checked out at' })}
        >
          <p className="text-sm">{checkedOutAtLabel}</p>
        </FieldRow>
      )}
    </div>
  );
}
